import axiosConfig from "./axiosConfig";
import {
  LandlordPromptRevision,
  PromptAddendumModel,
} from "./getLandlordResearchPrompt";

// Every prompt write returns the fresh active revision + full history, so the
// card re-renders from one shape whichever route it called.
export interface LandlordPromptWriteResponse {
  active: LandlordPromptRevision;
  revisions: LandlordPromptRevision[];
}

// The content fields an operator can set on a rule (mirrors BMA RULE_FIELDS).
// A partial object only touches the fields it names; `improvement` is
// operator-only and never reaches the model.
export interface PromptRuleFields {
  category?: string;
  title?: string;
  guidance?: string;
  why?: string;
  limits?: string;
  evidence?: string;
  improvement?: string;
}

const auth = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});

// Add (no ruleId) or update one rule.
export const saveLandlordPromptRule = async (
  authToken: string,
  body: {
    baseRevision: number;
    ruleId?: string | null;
    rule: PromptRuleFields;
    status?: string;
    note?: string;
  }
): Promise<LandlordPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/landlord-research-prompt/rule`,
    body,
    auth(authToken)
  );
  return data;
};

// Delete one rule outright (recoverable by restoring a revision that had it).
export const deleteLandlordPromptRule = async (
  authToken: string,
  body: { baseRevision: number; ruleId: string; note?: string }
): Promise<LandlordPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/landlord-research-prompt/rule/delete`,
    body,
    auth(authToken)
  );
  return data;
};

// Re-position rules to match `ruleIds` (rules omitted keep their order after).
export const reorderLandlordPromptRules = async (
  authToken: string,
  body: { baseRevision: number; ruleIds: string[]; note?: string }
): Promise<LandlordPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/landlord-research-prompt/reorder`,
    body,
    auth(authToken)
  );
  return data;
};

// Edit the free-text preamble half of the prompt.
export const saveLandlordPromptPreamble = async (
  authToken: string,
  body: { baseRevision: number; preamble: string; note?: string }
): Promise<LandlordPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/landlord-research-prompt/preamble`,
    body,
    auth(authToken)
  );
  return data;
};

// Whole-set save: replace the entire addendum in one revision (bulk path).
export const saveLandlordPromptAddendum = async (
  authToken: string,
  body: { baseRevision: number; addendum: PromptAddendumModel; note?: string }
): Promise<LandlordPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/landlord-research-prompt`,
    body,
    auth(authToken)
  );
  return data;
};

// Restore an earlier revision by appending a copy of it.
export const restoreLandlordPromptRevision = async (
  authToken: string,
  body: { baseRevision: number; revisionNumber: number; note?: string }
): Promise<LandlordPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/landlord-research-prompt/restore`,
    body,
    auth(authToken)
  );
  return data;
};
